import React, { useEffect, useMemo, useState } from 'react'
import StateCard, { StateCardSkeleton } from '../StatesPage/StateCard'

function groupByState(records) {
    const groups = new Map()

    records.forEach((record) => {
        const label = String(record.state || 'Unknown').trim().replace(/\s+/g, ' ').toUpperCase()
        if (!groups.has(label)) groups.set(label, { state: label, members: [] })
        groups.get(label).members.push(record)
    })

    return [...groups.values()]
        .map((group) => ({
            ...group,
            members: [...group.members].sort((a, b) =>
                String(a.name || '').localeCompare(String(b.name || ''), undefined, { sensitivity: 'base' })
            ),
        }))
        .sort((a, b) => a.state.localeCompare(b.state))
}

const COMMITTEE = [
    {
        role: 'Director General',
        name: 'Sanjeev Kumar',
        detail: 'Director, Xtremex Company',
    },
    {
        role: 'Chairman',
        name: 'Daya Chand Bhola',
        detail: 'International Referee & Judge',
    },
    {
        role: 'Admin. Secretary',
        name: 'Nishant Bhola',
    },
    {
        role: 'Treasurer',
        name: 'Nishank Mishra',
    },
    {
        role: 'Technical Chairman',
        name: 'Sajan P. P.',
    },
    {
        role: 'Women Commission Chairperson',
        name: 'Shalini Singh',
        detail: 'International Referee & Judge',
    },
    {
        role: 'Legal Advisor',
        name: 'Advocate Varun Mittal',
        detail: 'Director, Paper Asset Management',
    },
]

function CommitteeRow({ member, index }) {
    return (
        <li className="grid grid-cols-1 justify-items-center gap-1 py-6 text-center md:grid-cols-12 md:items-baseline md:justify-items-start md:gap-6 md:py-8 md:text-left">
            <span className="text-xs tabular-nums tracking-[0.2em] text-neutral-400 md:col-span-1">
                {String(index + 1).padStart(2, '0')}
            </span>
            <p className="text-[15px] uppercase tracking-[0.22em] text-red-600 protest md:col-span-4">
                {member.role}
            </p>
            <div className="md:col-span-7">
                <p className="interf text-xl font-semibold leading-tight text-neutral-900 md:text-[1.65rem]">
                    {member.name}
                </p>
                {member.detail && (
                    <p className="mt-1.5 text-sm leading-relaxed text-neutral-500">{member.detail}</p>
                )}
            </div>
        </li>
    )
}

function States() {
    const [statesData, setStatesData] = useState([])
    const [loading, setLoading] = useState(true)

    useEffect(() => {
        const fetchData = async () => {
            try {
                const response = await fetch(`${process.env.REACT_APP_MINF_BAC_BASE}/statecard`)
                const data = await response.json()
                setStatesData(Array.isArray(data) ? data : [])
            } catch (error) {
                console.error('Error fetching data:', error)
                setStatesData([])
            } finally {
                setLoading(false)
            }
        }
        fetchData()
    }, [])

    const stateGroups = useMemo(() => groupByState(statesData), [statesData])

    return (
        <section className="mb-16 bg-transparent">
            <div className="mx-5 mb-14 flex min-h-[34vh] items-center justify-center overflow-hidden rounded-lg bg-neutral-900 px-6 py-16 md:min-h-[30vh]">
                <div className="max-w-full text-center">
                    <p className="protest mb-4 text-[11px] uppercase tracking-[0.32em] text-red-500 sm:text-xs sm:tracking-[0.42em]">
                        WBC Amateur Muaythai India
                    </p>
                    <h1 className="interf text-[clamp(2.35rem,7vw,6rem)] leading-none tracking-[0.1em] text-white md:tracking-[0.14em]">
                        Committees
                    </h1>
                </div>
            </div>

            <div className="container mx-auto px-6 md:px-10">
                <div className="flex flex-col items-center gap-6 text-center md:flex-row md:items-end md:justify-between md:text-left">
                    <div>
                        <p className="protest text-xs uppercase tracking-[0.32em] text-red-600">Leadership</p>
                        <h2 className="interf mt-2 text-3xl text-neutral-900 md:text-5xl">Executive Committee</h2>
                    </div>
                    <p className="hidden pb-1 text-sm tabular-nums text-neutral-400 md:block">
                        {String(COMMITTEE.length).padStart(2, '0')} members
                    </p>
                </div>

                <ol className="mt-8 divide-y divide-neutral-300/90 border-y border-neutral-300/90">
                    {COMMITTEE.map((member, index) => (
                        <CommitteeRow key={member.role} member={member} index={index} />
                    ))}
                </ol>

                <div className="mt-20 text-center md:text-left">
                    <p className="protest text-xs uppercase tracking-[0.32em] text-red-600">Affiliates</p>
                    <h2 className="interf mt-2 text-3xl text-neutral-900 md:text-5xl">State Associations</h2>
                </div>

                {loading ? (
                    <ul className="mt-8 border-y border-neutral-300/90" aria-hidden="true">
                        {[1, 2, 1, 1].map((members, index) => (
                            <StateCardSkeleton key={index} index={index} members={members} />
                        ))}
                    </ul>
                ) : stateGroups.length === 0 ? (
                    <p className="mt-8 border-y border-neutral-300/90 py-12 text-center text-sm text-neutral-500">
                        State associations will appear here once they are published.
                    </p>
                ) : (
                    <ul className="states-fade mt-8 border-y border-neutral-300/90">
                        {stateGroups.map((group) => (
                            <StateCard key={group.state} group={group} />
                        ))}
                    </ul>
                )}
            </div>
        </section>
    )
}

export default States
